import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChatBot } from '../components/ChatBot';
import WebScraper from '../components/WebScraper';
import Footer from '../components/Footer';
import { uploadService } from '../services/uploadService';
import { tenantService, TenantData } from '../services/tenantService';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const FileUploadSection = ({ canUpload, hasTenantData, handleSubmit, fileInputRef, file, setFile, errors, setErrors, isLoading, isExtracting, tenantcode, setTenantcode, expandedSection, setExpandedSection }: any) => {
  const isCollapsed = expandedSection !== 'upload';
  
  const handleToggle = () => {
    setExpandedSection(expandedSection === 'upload' ? null : 'upload');
  };
  
  return (
    <div className={`collapsible-section ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="section-header" onClick={handleToggle}>
        <span>File Upload</span>
        <span className={`chevron ${isCollapsed ? 'rotated' : ''}`}>▼</span>
      </div>
      <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
        {canUpload && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="file">Select File</label>
              <input
                ref={fileInputRef}
                id="file"
                type="file"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  setFile(selectedFile);
                  if (errors.file) {
                    setErrors({ ...errors, file: undefined });
                  }
                }}
                accept=".txt,.csv,.xlsx,.docx,.doc,.pdf"
                required
                disabled={isLoading}
              />
              {file && (
                <div style={{color: '#28a745', fontSize: '0.875rem', marginTop: '5px'}}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
              {errors.file && <div className="error-text">{errors.file}</div>}
              {isExtracting && (
                <div style={{color: '#007bff', fontSize: '0.875rem', marginTop: '5px'}}>
                  Extracting text from document...
                </div>
              )}
            </div>
            
            <div className="rules-box">
              <p>Upload Rules:</p>
              <ul>
                <li>File size must be under 2MB</li>
                <li>No images should be present in documents</li>
                <li>Supported formats: TXT, CSV, XLSX, DOCX, DOC, PDF</li>
                <li><strong>Note:</strong> PDF and Word files will be converted to .txt format</li>
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="tenantcode">Tenant Code</label>
              <input
                id="tenantcode"
                type="text"
                value={tenantcode}
                onChange={(e) => setTenantcode(e.target.value)}
                placeholder="Enter tenant code"
                required
              />
              {errors.tenantcode && <div className="error-text">{errors.tenantcode}</div>}
            </div>

            <button type="submit" className="btn" disabled={isLoading} style={{width: '100%'}}>
              {isExtracting ? 'Extracting Text...' : isLoading ? 'Uploading...' : 'Upload File'}
            </button>
          </form>
        )}

        {!canUpload && hasTenantData && (
          <div style={{textAlign: 'center', padding: '30px', background: '#f8f9fa', borderRadius: '8px'}}>
            <p style={{color: '#6c757d'}}>Upload limit reached (5 files maximum per tenant)</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Upload: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    const userTenants = await tenantService.getUserTenants();
    setTenants(userTenants);
    setHasTenantData(userTenants.length > 0);
    
    if (userTenants.length === 1) {
      setSelectedTenant(userTenants[0].tenantcode);
      setCanUpload(userTenants[0].fileCount < 5);
    } else if (userTenants.length > 1) {
      const hasUploadCapacity = userTenants.some(t => t.fileCount < 5);
      setCanUpload(hasUploadCapacity);
    }
  };

  const handleTenantSelect = (tenantcode: string) => {
    setSelectedTenant(tenantcode);
    const tenant = tenants.find(t => t.tenantcode === tenantcode);
    setCanUpload(tenant ? tenant.fileCount < 5 : false);
  };
  const [file, setFile] = useState<File | null>(null);
  const [tenantcode, setTenantcode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [errors, setErrors] = useState<{ file?: string; tenantcode?: string }>({});

  const [uploadedTenantCode, setUploadedTenantCode] = useState('');
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [canUpload, setCanUpload] = useState(true);
  const [hasTenantData, setHasTenantData] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'upload' | 'scraper' | null>('upload');

  const validateForm = (): boolean => {
    const newErrors: { file?: string; tenantcode?: string } = {};
    
    if (!file) {
      newErrors.file = 'Please select a file';
    } else {
      const allowedTypes = [
        'text/plain',
        'text/csv', 
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/pdf'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        newErrors.file = 'Only txt, csv, xlsx, docx, doc, and pdf files are allowed';
      } else if (file.size > 2 * 1024 * 1024) {
        newErrors.file = 'File size must be under 2MB';
      }
    }
    
    if (!tenantcode) {
      newErrors.tenantcode = 'Tenant code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted');
    setStatus(null);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('File:', file, 'Tenant code:', tenantcode);

    // Show extraction status for PDF and Word files
    if (file && (file.type === 'application/pdf' || 
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword')) {
      setIsExtracting(true);
    }

    setIsLoading(true);

    try {
      console.log('Getting auth session...');
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      const userEmail = (payload?.email || payload?.['cognito:username'] || payload?.username || payload?.sub) as string;
      console.log('User email:', userEmail);
      
      console.log('Uploading file...');
      await uploadService.uploadFile(file!, tenantcode, userEmail);
      console.log('File uploaded successfully');
      
      console.log('Updating tenant mapping...');
      await tenantService.updateTenantMapping(tenantcode);
      console.log('Tenant mapping updated');
      
      console.log('Loading tenant data...');
      await loadTenantData();
      
      const successMessage = isExtracting ? 
        'Text extracted and uploaded successfully as .txt file!' : 
        'File uploaded successfully!';
      setStatus({ type: 'success', message: successMessage });
      setUploadedTenantCode(tenantcode);
      setFile(null);
      setTenantcode('');
      setErrors({});
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Upload error:', err);
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to upload file',
      });
    } finally {
      setIsLoading(false);
      setIsExtracting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleScrapedSummary = async (summary: string, tenantCode: string) => {
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      const userEmail = (payload?.email || payload?.['cognito:username'] || payload?.username || payload?.sub) as string;
      
      const blob = new Blob([summary], { type: 'text/plain' });
      const file = new File([blob], 'website-summary.txt', { type: 'text/plain' });
      
      await uploadService.uploadFile(file, tenantCode, userEmail);
      await loadTenantData();
      
      setStatus({ type: 'success', message: 'Website summary uploaded successfully!' });
      setUploadedTenantCode(tenantCode);
    } catch (err) {
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to upload summary',
      });
    }
  };



  const WebScraperSection = ({ onSummaryGenerated }: any) => {
    const isCollapsed = expandedSection !== 'scraper';
    
    const handleToggle = () => {
      setExpandedSection(expandedSection === 'scraper' ? null : 'scraper');
    };
    
    return (
      <div className={`collapsible-section ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="section-header" onClick={handleToggle}>
          <span>Website Scraper</span>
          <span className={`chevron ${isCollapsed ? 'rotated' : ''}`}>▼</span>
        </div>
        <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
          <WebScraper onSummaryGenerated={onSummaryGenerated} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <style>{`
        :root {
          --primary: #3a86ff;
          --secondary: #8338ec;
          --accent: #ff006e;
          --light: #f8f9fa;
          --dark: #212529;
          --success: #38b000;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
        }
        body {
          color: var(--dark);
          background-color: white;
          line-height: 1.6;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .btn {
          display: inline-block;
          padding: 12px 24px;
          background: var(--primary);
          color: white;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }
        .btn:hover {
          background: var(--secondary);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          padding: 20px 0;
          background-color: white;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
        }
        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo {
          font-size: 1.8rem;
          font-weight: 800;
          color: var(--primary);
          text-decoration: none;
        }
        .logo span {
          color: var(--secondary);
        }
        .main-content {
          padding: 120px 0 40px;
          min-height: calc(100vh - 80px);
        }
        .upload-container {
          max-width: ${hasTenantData || uploadedTenantCode ? '1200px' : '600px'};
          margin: 0 auto;
          padding: 0 20px;
        }
        .grid {
          display: grid;
          gap: 40px;
          grid-template-columns: ${hasTenantData || uploadedTenantCode ? '1fr 1fr' : '1fr'};
        }
        .left-column {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .collapsible-section {
          background: white;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }
        .section-header {
          padding: 20px 30px;
          background: white;
          color: var(--primary);
          border: 2px solid var(--primary);
          border-radius: 15px 15px 0 0;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
        }
        .collapsible-section.collapsed .section-header {
          border-radius: 15px;
        }
        .section-header:hover {
          background: #f8f9ff;
        }
        .section-content {
          padding: 30px;
          display: block;
        }
        .section-content.collapsed {
          display: none;
        }
        .chevron {
          transition: transform 0.3s ease;
        }
        .chevron.rotated {
          transform: rotate(180deg);
        }
        .form-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: var(--dark);
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }
        .form-group input:focus,
        .form-group select:focus {
          border-color: var(--primary);
          outline: none;
        }
        .error-text {
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 5px;
        }
        .success-alert {
          background: #d4edda;
          color: #155724;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          border: 1px solid #c3e6cb;
        }
        .error-alert {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          border: 1px solid #f5c6cb;
        }
        .rules-box {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .rules-box p {
          font-weight: 500;
          margin-bottom: 10px;
        }
        .rules-box ul {
          margin: 0;
          padding-left: 20px;
        }
        .rules-box li {
          margin-bottom: 5px;
        }
        @media (max-width: 768px) {
          .grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      <header>
        <div className="container">
          <nav className="navbar">
            <Link to="/" className="logo">Nexly<span>Digital</span></Link>
            <button onClick={handleSignOut} className="btn">Sign Out</button>
          </nav>
        </div>
      </header>

      <div className="main-content">
        <div className="upload-container">
          <h1 style={{textAlign: 'center', marginBottom: '40px', color: 'var(--primary)', fontSize: '2.5rem'}}>Demo Application</h1>
          
          {status && (
            <div className={status.type === 'success' ? 'success-alert' : 'error-alert'}>
              {status.message}
            </div>
          )}

          <div className="grid">
            <div className="left-column">
              <FileUploadSection 
                canUpload={canUpload}
                hasTenantData={hasTenantData}
                handleSubmit={handleSubmit}
                fileInputRef={fileInputRef}
                file={file}
                setFile={setFile}
                errors={errors}
                setErrors={setErrors}
                isLoading={isLoading}
                isExtracting={isExtracting}
                tenantcode={tenantcode}
                setTenantcode={setTenantcode}
                expandedSection={expandedSection}
                setExpandedSection={setExpandedSection}
              />
              <WebScraperSection onSummaryGenerated={handleScrapedSummary} />
            </div>

            {(hasTenantData || uploadedTenantCode) && (
              <div>
                <ChatBot 
                  tenantcode={selectedTenant || uploadedTenantCode}
                  tenants={tenants}
                  onTenantChange={handleTenantSelect}
                  onTenantDeleted={async () => {
                    await loadTenantData();
                    setSelectedTenant('');
                    setUploadedTenantCode('');
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Upload;